import React, { useEffect, useMemo, useRef, useState } from 'react';

/* ── Screenshot gallery ───────────────────────── */

const Gallery: React.FC<{ images: string[] }> = ({ images }) => {
  const primaryImage = images[0];
  const secondaryImages = images.slice(1);

  return (
    <div className="project-gallery">
      <div className="project-gallery-feature">
        <img
          src={primaryImage}
          alt="Primary project screenshot"
          loading="lazy"
          className="project-gallery-image"
        />
      </div>
      {secondaryImages.length > 0 && (
        <div className={`project-gallery-grid project-gallery-grid-${Math.min(secondaryImages.length, 4)}`}>
          {secondaryImages.map((image, index) => (
            <div key={image} className="project-gallery-tile">
              <img
                src={image}
                alt={`Screenshot ${index + 2}`}
                loading="lazy"
                className="project-gallery-image"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── SVG Diagrams ─────────────────────────────── */

const Grid: React.FC<{ w?: number; h?: number }> = ({ w = 360, h = 80 }) => (
  <>
    {Array.from({ length: Math.ceil(w / 20) + 1 }, (_, i) => (
      <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20} y2={h} stroke="#e8e8e2" strokeWidth="0.4" />
    ))}
    {Array.from({ length: Math.ceil(h / 20) + 1 }, (_, i) => (
      <line key={`h${i}`} x1="0" y1={i * 20} x2={w} y2={i * 20} stroke="#e8e8e2" strokeWidth="0.4" />
    ))}
  </>
);

/* ── RenderGraph: GBuffer → Shadow → Light → TAA → Tonemap → Present */
const RenderGraphSVG: React.FC = () => {
  const W = 340, H = 84;
  const passes = [
    { label: 'Depth', x: 22, y: 12, w: 42, h: 14 },
    { label: 'GBuffer', x: 80, y: 12, w: 48, h: 14 },
    { label: 'Shadow', x: 22, y: 40, w: 44, h: 14 },
    { label: 'Lighting', x: 140, y: 26, w: 52, h: 16 },
    { label: 'TAA', x: 210, y: 18, w: 34, h: 14 },
    { label: 'Tonemap', x: 210, y: 46, w: 48, h: 14 },
    { label: 'Present', x: 274, y: 32, w: 42, h: 16 },
  ] as const;
  const resources = [
    { label: 'depth', x: 74, y: 54, w: 32, h: 10 },
    { label: 'gbuf', x: 110, y: 54, w: 30, h: 10 },
    { label: 'shadow atlas', x: 78, y: 70, w: 54, h: 10 },
    { label: 'history', x: 214, y: 68, w: 36, h: 10 },
  ] as const;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#eeeee9" />
      <Grid w={W} h={H} />
      <defs>
        <marker id="arr1" markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
          <path d="M0,0 L4,2 L0,4 Z" fill="#aaa" />
        </marker>
      </defs>
      <text x="10" y="9" fontFamily="'JetBrains Mono', monospace" fontSize="6.2" fill="#666">
        Vulkan frame pipeline
      </text>
      {passes.map((pass) => (
        <g key={pass.label}>
          <rect x={pass.x} y={pass.y} width={pass.w} height={pass.h} rx="2"
            fill={pass.label === 'Present' ? '#252525' : '#f4f4ef'}
            stroke={pass.label === 'Present' ? '#252525' : '#c7c7c0'} strokeWidth="0.7" />
          <text x={pass.x + pass.w / 2} y={pass.y + pass.h / 2 + 2.5} textAnchor="middle"
            fontFamily="'JetBrains Mono', monospace" fontSize="6.4"
            fill={pass.label === 'Present' ? '#f2f2f0' : '#555'}>{pass.label}</text>
        </g>
      ))}
      {resources.map((res) => (
        <g key={res.label}>
          <rect x={res.x} y={res.y} width={res.w} height={res.h} rx="1.5"
            fill="#e6e6de" stroke="#cdcdc5" strokeWidth="0.6" strokeDasharray="2 1.5" />
          <text x={res.x + res.w / 2} y={res.y + res.h / 2 + 2.1} textAnchor="middle"
            fontFamily="'JetBrains Mono', monospace" fontSize="5.3" fill="#777">{res.label}</text>
        </g>
      ))}
      <line x1="64" y1="19" x2="80" y2="19" stroke="#b1b1a9" strokeWidth="0.8" markerEnd="url(#arr1)" />
      <line x1="128" y1="19" x2="140" y2="30" stroke="#b1b1a9" strokeWidth="0.8" markerEnd="url(#arr1)" />
      <line x1="66" y1="47" x2="140" y2="38" stroke="#b1b1a9" strokeWidth="0.8" markerEnd="url(#arr1)" />
      <line x1="192" y1="34" x2="210" y2="25" stroke="#b1b1a9" strokeWidth="0.8" markerEnd="url(#arr1)" />
      <line x1="192" y1="34" x2="210" y2="53" stroke="#b1b1a9" strokeWidth="0.8" markerEnd="url(#arr1)" />
      <line x1="244" y1="25" x2="274" y2="38" stroke="#b1b1a9" strokeWidth="0.8" markerEnd="url(#arr1)" />
      <line x1="258" y1="53" x2="274" y2="42" stroke="#b1b1a9" strokeWidth="0.8" markerEnd="url(#arr1)" />
      <line x1="88" y1="54" x2="88" y2="26" stroke="#c8a76c" strokeWidth="0.65" markerEnd="url(#arr1)" />
      <line x1="125" y1="54" x2="155" y2="42" stroke="#c8a76c" strokeWidth="0.65" markerEnd="url(#arr1)" />
      <line x1="112" y1="70" x2="44" y2="54" stroke="#c8a76c" strokeWidth="0.65" markerEnd="url(#arr1)" />
      <line x1="232" y1="68" x2="228" y2="32" stroke="#c8a76c" strokeWidth="0.65" markerEnd="url(#arr1)" />
      <text x="92" y="49" fontFamily="'JetBrains Mono', monospace" fontSize="4.4" fill="#9a7a44">
        reads depth
      </text>
      <text x="144" y="66" fontFamily="'JetBrains Mono', monospace" fontSize="4.4" fill="#9a7a44">
        shadow map
      </text>
      <text x="238" y="65" fontFamily="'JetBrains Mono', monospace" fontSize="4.4" fill="#9a7a44">
        writes history
      </text>
    </svg>
  );
};

/* ── Path Tracer: camera → sphere hit → reflect / shadow / scatter */
const PathTracerSVG: React.FC = () => {
  const W = 340, H = 74;
  const cx = 158, cy = 30, r = 20;
  const hitX = Math.round(cx + r * Math.cos((210 * Math.PI) / 180));
  const hitY = Math.round(cy + r * Math.sin((210 * Math.PI) / 180));
  const nScale = 13;
  const nTipX = Math.round(hitX + ((hitX - cx) / r) * nScale);
  const nTipY = Math.round(hitY + ((hitY - cy) / r) * nScale);
  const lightX = 314, lightY = 8;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#eeeee9" />
      <Grid w={W} h={H} />
      <defs>
        <marker id="arrPT" markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
          <path d="M0,0 L4,2 L0,4 Z" fill="#888" />
        </marker>
        <marker id="arrN" markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
          <path d="M0,0 L4,2 L0,4 Z" fill="#c05818" />
        </marker>
        <marker id="arrDim" markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
          <path d="M0,0 L4,2 L0,4 Z" fill="#ccc" />
        </marker>
      </defs>
      <text x="10" y="9" fontFamily="'JetBrains Mono', monospace" fontSize="6.2" fill="#666">
        Vulkan ray tracing pipeline
      </text>
      <circle cx={cx} cy={cy} r={r} fill="#f0f0ea" stroke="#c0c0b8" strokeWidth="1" />
      <circle cx={lightX} cy={lightY} r="5" fill="#f6eecc" stroke="#c8a830" strokeWidth="0.8" />
      <text x={lightX} y={lightY + 3} textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace" fontSize="5" fill="#9a7820">L</text>
      <circle cx="10" cy="17" r="3" fill="none" stroke="#888" strokeWidth="0.9" />
      <text x="17" y="16" fontFamily="'JetBrains Mono', monospace" fontSize="6" fill="#999">cam</text>
      <line x1="13" y1="17" x2={hitX - 1} y2={hitY}
        stroke="#888" strokeWidth="0.9" strokeDasharray="3 2" markerEnd="url(#arrPT)" />
      <line x1={hitX} y1={hitY} x2={nTipX} y2={nTipY}
        stroke="#c05818" strokeWidth="1" markerEnd="url(#arrN)" />
      <text x={nTipX - 1} y={nTipY - 2} fontFamily="'JetBrains Mono', monospace" fontSize="6" fill="#c05818">N</text>
      <line x1={hitX} y1={hitY} x2={hitX + 66} y2={hitY - 16}
        stroke="#888" strokeWidth="0.9" strokeDasharray="3 2" markerEnd="url(#arrPT)" />
      <line x1={hitX} y1={hitY} x2={lightX - 5} y2={lightY + 2}
        stroke="#ccc" strokeWidth="0.7" strokeDasharray="2 2" markerEnd="url(#arrDim)" />
      <line x1={hitX} y1={hitY} x2={hitX - 28} y2={hitY + 28}
        stroke="#ccc" strokeWidth="0.7" strokeDasharray="2 2" markerEnd="url(#arrDim)" />
      <circle cx={hitX} cy={hitY} r="1.8" fill="#444" />
      <text x="24" y="16" fontFamily="'JetBrains Mono', monospace" fontSize="5" fill="#888">camera ray</text>
      <text x={hitX + 8} y={hitY - 7} fontFamily="'JetBrains Mono', monospace" fontSize="5" fill="#888">hit shader</text>
      <text x={hitX + 40} y={hitY - 18} fontFamily="'JetBrains Mono', monospace" fontSize="5" fill="#888">material eval</text>
      <text x={hitX + 50} y={hitY + 3} fontFamily="'JetBrains Mono', monospace" fontSize="5" fill="#aaa">shadow ray</text>
      <rect x="220" y="58" width="104" height="14" rx="1.5" fill="#f2f2ed" stroke="#ccccbf" strokeWidth="0.6" />
      <text x="228" y="67" fontFamily="'JetBrains Mono', monospace" fontSize="5" fill="#777">BVH traversal</text>
    </svg>
  );
};

/* ── GPU Benchmark: horizontal bar chart */
const BenchmarkSVG: React.FC = () => {
  const bars = [
    { label: 'wg=8', val: 0.38, ms: '1.84 ms' },
    { label: 'wg=16', val: 0.56, ms: '1.32 ms' },
    { label: 'wg=32', val: 0.74, ms: '0.96 ms' },
    { label: 'wg=64', val: 0.92, ms: '0.71 ms' },
  ];
  const W = 320, H = 72;
  const barX = 72, barMaxW = 170, oy = 16, rowH = 12;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#eeeee9" />
      <Grid w={W} h={H} />
      <text x="10" y="9" fontFamily="'JetBrains Mono', monospace" fontSize="6.2" fill="#666">
        Kernel throughput sweep
      </text>
      <text x="10" y="17" fontFamily="'JetBrains Mono', monospace" fontSize="5.2" fill="#999">
        OpenCL benchmark
      </text>
      <line x1={barX} y1={oy} x2={barX} y2={oy + bars.length * rowH + 2} stroke="#ccc" strokeWidth="0.6" />
      <line x1={barX + barMaxW} y1={oy - 2} x2={barX + barMaxW} y2={oy + bars.length * rowH + 2}
        stroke="#e0e0d8" strokeWidth="0.5" strokeDasharray="2 2" />
      <text x={barX + barMaxW} y={oy - 3} textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace" fontSize="5.5" fill="#ccc">best</text>
      {bars.map((b, i) => {
        const y = oy + i * rowH;
        const w = b.val * barMaxW;
        return (
          <g key={i}>
            <text x={barX - 3} y={y + rowH - 2} textAnchor="end"
              fontFamily="'JetBrains Mono', monospace" fontSize="6.5" fill="#888">{b.label}</text>
            <rect x={barX} y={y + 1} width={w} height={rowH - 3} rx="1"
              fill="#333" opacity={0.4 + i * 0.12} />
            <text x={barX + w + 5} y={y + rowH - 2}
              fontFamily="'JetBrains Mono', monospace" fontSize="5.6" fill="#777">
              {b.ms}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/* ── Transformer: causal attention heatmap + architecture stack */
const TransformerSVG: React.FC = () => {
  const W = 420, H = 100;
  const size = 6, cell = 10, gap = 2;
  const ox = 16, oy = 14;
  const matW = size * (cell + gap) - gap; // 70px
  const weights = Array.from({ length: size }, (_, i) =>
    Array.from({ length: size }, (_, j) => {
      if (j > i) return -1;
      const dist = i - j;
      return dist === 0 ? 0.88 : Math.max(0.07, 0.82 * Math.exp(-0.42 * dist));
    })
  );
  const bx = ox + matW + 20; // 106
  const boxW = W - bx - 8;   // 306
  const components = ['Embedding + BPE','Positional Enc.','LN + MHA (×6)','LN + FFN (×6)','Linear + Softmax'];
  const boxH = 14, boxGap = 3;
  // Total stack height: 5*14 + 4*3 = 82. Bottom: oy+82=96. Caption at H-4=96. Tight but ok.
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#eeeee9" />
      <Grid w={W} h={H} />
      <defs>
        <marker id="arrTF" markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
          <path d="M0,0 L4,2 L0,4 Z" fill="#bbb" />
        </marker>
      </defs>
      {/* Axis labels */}
      <text x={ox + matW / 2} y="8" textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace" fontSize="6" fill="#bbb">K</text>
      <text x={ox - 5} y={oy + matW / 2 + 2.5} textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace" fontSize="6" fill="#bbb"
        transform={`rotate(-90, ${ox - 5}, ${oy + matW / 2})`}>Q</text>
      {/* Heatmap */}
      {weights.map((row, i) => row.map((w, j) => (
        <rect key={`${i}-${j}`}
          x={ox + j * (cell + gap)} y={oy + i * (cell + gap)}
          width={cell} height={cell} rx="0.8"
          fill={w < 0 ? '#e6e6e0' : `rgba(28,28,28,${w.toFixed(2)})`}
        />
      )))}
      {/* Token labels */}
      {Array.from({ length: size }, (_, i) => (
        <text key={i} x={ox + i * (cell + gap) + cell / 2} y={oy + matW + 8}
          textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="5.5" fill="#bbb">
          t{i}
        </text>
      ))}
      <text x={ox} y={H - 3} fontFamily="'JetBrains Mono', monospace" fontSize="6" fill="#bbb">
        Causal self-attention
      </text>
      {/* Divider */}
      <line x1={bx - 8} y1="6" x2={bx - 8} y2={H - 6}
        stroke="#ddd" strokeWidth="0.7" strokeDasharray="2 3" />
      {/* Stack */}
      {components.map((label, i) => {
        const y = oy + i * (boxH + boxGap);
        const dark = i === 2 || i === 3;
        return (
          <g key={i}>
            <rect x={bx} y={y} width={boxW} height={boxH} rx="1.5"
              fill={dark ? '#252525' : '#f2f2ed'} stroke={dark ? '#333' : '#ccc'} strokeWidth="0.7" />
            <text x={bx + boxW / 2} y={y + boxH / 2 + 3} textAnchor="middle"
              fontFamily="'JetBrains Mono', monospace" fontSize="7"
              fill={dark ? '#eee' : '#555'}>{label}</text>
            {i < components.length - 1 && (
              <line x1={bx + boxW / 2} y1={y + boxH} x2={bx + boxW / 2} y2={y + boxH + boxGap}
                stroke="#bbb" strokeWidth="0.7" markerEnd="url(#arrTF)" />
            )}
          </g>
        );
      })}
      {/* Heatmap → MHA connection */}
      <line x1={ox + matW + 1} y1={oy + 2 * (cell + gap) + cell / 2}
        x2={bx - 8} y2={oy + 2 * (boxH + boxGap) + boxH / 2}
        stroke="#bbb" strokeWidth="0.7" strokeDasharray="3 2" markerEnd="url(#arrTF)" />
    </svg>
  );
};

/* ── CUDA: memory hierarchy (H100 real specs — no made-up data) */
const CudaVizSVG: React.FC = () => {
  const W = 420, H = 90;
  // All values from NVIDIA H100 SXM5 whitepaper / CUDA programming guide
  const levels = [
    { label: 'Registers',   size: '256 KB/SM',  lat: '1 cy',    bw: '~19 TB/s' },
    { label: 'Shared / L1', size: '228 KB/SM',  lat: '~30 cy',  bw: '~30 TB/s' },
    { label: 'L2 Cache',    size: '50 MB',       lat: '~200 cy', bw: '~5 TB/s'  },
    { label: 'HBM3',        size: '80 GB',       lat: '~500 cy', bw: '3.35 TB/s'},
  ];
  const lH = 14, lGap = 3, cx = 192, minW = 68, step = 54, oy = 10;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#eeeee9" />
      <Grid w={W} h={H} />
      {/* Column headers */}
      <text x={cx} y={oy - 3} textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace" fontSize="5.5" fill="#ccc">memory level</text>
      <text x={cx + minW/2 + 3*step/2 + 38} y={oy - 3} textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace" fontSize="5.5" fill="#ccc">latency</text>
      {levels.map((lv, i) => {
        const bw = minW + i * step;
        const x = cx - bw / 2;
        const y = oy + i * (lH + lGap);
        // Darkest (fastest) at top, lightest (slowest) at bottom
        const alpha = 0.82 - i * 0.17;
        return (
          <g key={i}>
            {/* Bar */}
            <rect x={x} y={y} width={bw} height={lH} rx="1.5"
              fill={`rgba(34,34,34,${alpha.toFixed(2)})`} />
            {/* Level name inside bar */}
            <text x={cx} y={y + lH / 2 + 3} textAnchor="middle"
              fontFamily="'JetBrains Mono', monospace" fontSize="7"
              fill={i >= 2 ? '#ddd' : '#f0f0f0'}>{lv.label}</text>
            {/* Size — left of bar */}
            <text x={x - 3} y={y + lH / 2 + 3} textAnchor="end"
              fontFamily="'JetBrains Mono', monospace" fontSize="6" fill="#aaa">{lv.size}</text>
            {/* Latency — right of bar */}
            <text x={x + bw + 4} y={y + lH / 2 + 3}
              fontFamily="'JetBrains Mono', monospace" fontSize="6.5" fill="#888">{lv.lat}</text>
            {/* Bandwidth — further right */}
            <text x={x + bw + 46} y={y + lH / 2 + 3}
              fontFamily="'JetBrains Mono', monospace" fontSize="6" fill="#bbb">{lv.bw}</text>
          </g>
        );
      })}
      {/* Vertical fast→slow arrow on far left */}
      <text x="6" y={oy + 6} fontFamily="'JetBrains Mono', monospace" fontSize="5.5" fill="#ccc">fast</text>
      <line x1="8" y1={oy + 9} x2="8" y2={oy + 4*(lH+lGap) - lGap - 4} stroke="#ddd" strokeWidth="0.6" />
      <text x="6" y={oy + 4*(lH+lGap) + 3} fontFamily="'JetBrains Mono', monospace" fontSize="5.5" fill="#ccc">slow</text>
      <text x="8" y={H - 4} fontFamily="'JetBrains Mono', monospace" fontSize="6.5" fill="#bbb">
        CUDA memory hierarchy · H100 SXM5 · latency and bandwidth per level
      </text>
    </svg>
  );
};

/* ── Matrix Multiply: transform chain S·R·T = M with 3D cube visual */
const MatrixMulSVG: React.FC = () => {
  const W = 310, H = 68;
  const cs = 4.5, cg = 0.5, step = 5.0, n = 4;
  const mSz = n * step - cg; // 19.5
  const y0 = 13, opY = y0 + mSz / 2 + 1.5, lblY = y0 + mSz + 7.5;

  // Cell fill functions (color-coded by transform type)
  const sf = (r: number, c: number) => r === c ? '#9aaa98' : '#ddddd4';           // scale: diagonal
  const rf = (r: number, c: number) => r < 3 && c < 3 ? '#9898b4' : r === c ? '#ccccda' : '#ddddd4'; // rotate: 3×3 block
  const tff = (r: number, c: number) => r < 3 && c === 3 ? '#b89888' : r === c ? '#d0cccc' : '#ddddd4'; // translate: right col
  const mff = () => '#b8b8b0';                                                      // result M: uniform

  const renderMat = (x: number, ff: (r: number, c: number) => string) =>
    Array.from({ length: n }, (_, r) =>
      Array.from({ length: n }, (_, c) => (
        <rect key={`${r}-${c}`} x={x + c * step} y={y0 + r * step}
          width={cs} height={cs} fill={ff(r, c)} rx="0.5" />
      ))
    );

  // Chain: S  ·  R  ·  T  =  M
  const xS = 10;
  const xD1 = xS + mSz + 3;   // 32.5
  const xR  = xD1 + 9;        // 41.5
  const xD2 = xR + mSz + 3;   // 64
  const xT  = xD2 + 9;        // 73
  const xEq = xT + mSz + 3;   // 95.5
  const xM  = xEq + 10;       // 105.5 — end at 125

  // Oblique 3D cube (right panel)
  const bx = 168, by = 14, bw = 22, bh = 22, bd = 7;
  // Faces
  const frontPts = `${bx},${by} ${bx+bw},${by} ${bx+bw},${by+bh} ${bx},${by+bh}`;
  const topPts   = `${bx},${by} ${bx+bd},${by-bd} ${bx+bw+bd},${by-bd} ${bx+bw},${by}`;
  const rightPts = `${bx+bw},${by} ${bx+bw+bd},${by-bd} ${bx+bw+bd},${by+bh-bd} ${bx+bw},${by+bh}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#eeeee9" />
      <Grid w={W} h={H} />

      {/* ── S (scale) ── */}
      {renderMat(xS, sf)}
      <text x={xS + mSz/2} y={y0 - 3} textAnchor="middle"
        fontFamily="'JetBrains Mono',monospace" fontSize="6.5" fontStyle="italic" fill="#888">S</text>
      <text x={xS + mSz/2} y={lblY} textAnchor="middle"
        fontFamily="'JetBrains Mono',monospace" fontSize="5.5" fill="#aaa">scale</text>

      <text x={xD1 + 4.5} y={opY} textAnchor="middle"
        fontFamily="Georgia,serif" fontSize="12" fill="#c4c4bc">·</text>

      {/* ── R (rotate) ── */}
      {renderMat(xR, rf)}
      <text x={xR + mSz/2} y={y0 - 3} textAnchor="middle"
        fontFamily="'JetBrains Mono',monospace" fontSize="6.5" fontStyle="italic" fill="#888">R</text>
      <text x={xR + mSz/2} y={lblY} textAnchor="middle"
        fontFamily="'JetBrains Mono',monospace" fontSize="5.5" fill="#aaa">rotate</text>

      <text x={xD2 + 4.5} y={opY} textAnchor="middle"
        fontFamily="Georgia,serif" fontSize="12" fill="#c4c4bc">·</text>

      {/* ── T (translate) ── */}
      {renderMat(xT, tff)}
      <text x={xT + mSz/2} y={y0 - 3} textAnchor="middle"
        fontFamily="'JetBrains Mono',monospace" fontSize="6.5" fontStyle="italic" fill="#888">T</text>
      <text x={xT + mSz/2} y={lblY} textAnchor="middle"
        fontFamily="'JetBrains Mono',monospace" fontSize="5.5" fill="#aaa">translate</text>

      <text x={xEq + 5} y={opY} textAnchor="middle"
        fontFamily="'JetBrains Mono',monospace" fontSize="9" fill="#bbb">=</text>

      {/* ── M (result) ── */}
      {renderMat(xM, mff)}
      <text x={xM + mSz/2} y={y0 - 3} textAnchor="middle"
        fontFamily="'JetBrains Mono',monospace" fontSize="6.5" fontStyle="italic" fontWeight="600" fill="#777">M</text>

      {/* Separator */}
      <line x1={140} y1={6} x2={140} y2={H - 8} stroke="#ccccc4" strokeWidth="0.5" strokeDasharray="2,2" />

      {/* ── 3D Cube ── */}
      <polygon points={frontPts} fill="#d4d4cc" stroke="#aaaaaa" strokeWidth="0.6" />
      <polygon points={topPts}   fill="#e2e2da" stroke="#aaaaaa" strokeWidth="0.6" />
      <polygon points={rightPts} fill="#bebeb6" stroke="#aaaaaa" strokeWidth="0.6" />

      {/* X axis */}
      <line x1={bx} y1={by+bh} x2={bx+13} y2={by+bh} stroke="#b08888" strokeWidth="0.8" />
      <text x={bx+15} y={by+bh+2} fontFamily="'JetBrains Mono',monospace" fontSize="5.5" fill="#b08888">x</text>
      {/* Y axis */}
      <line x1={bx} y1={by+bh} x2={bx} y2={by+bh-13} stroke="#88aa88" strokeWidth="0.8" />
      <text x={bx-7} y={by+bh-13} fontFamily="'JetBrains Mono',monospace" fontSize="5.5" fill="#88aa88">y</text>
      {/* Z axis (depth — lower-left) */}
      <line x1={bx} y1={by+bh} x2={bx-7} y2={by+bh+3.5} stroke="#8888b0" strokeWidth="0.8" />
      <text x={bx-15} y={by+bh+6} fontFamily="'JetBrains Mono',monospace" fontSize="5.5" fill="#8888b0">z</text>

      {/* Annotations right of cube */}
      <text x={bx+bw+bd+8} y={by+5}
        fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#999">v' = M·v</text>
      <text x={bx+bw+bd+8} y={by+14}
        fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#aaa">4×4 · 3×3</text>
      <text x={bx+bw+bd+8} y={by+23}
        fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#bbb">Three.js</text>

      {/* Caption */}
      <text x={8} y={H - 4} fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#bbb">
        transform chain · M = T·R·S
      </text>
    </svg>
  );
};

/* ── CUDA GEMM: shared memory tiling diagram ──── */
const CodeCudaEngineSVG: React.FC = () => {
  const W = 340, H = 94;
  const ax = 16, ay = 16, aw = 28, ah = 42;
  const sx = 72, sy = 16, sw = 32, sh = 42;
  const tx = 132, ty = 16, tw = 44, th = 42;
  const ox = 204, oy = 16, ow = 40, oh = 42;
  const strategies = [
    { label: 'naive', v: 0.28 },
    { label: 'coal', v: 0.52 },
    { label: 'smem', v: 0.79 },
    { label: 'blk', v: 0.92 },
  ];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#eeeee9" />
      <Grid w={W} h={H} />
      <defs>
        <marker id="arrGemm" markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
          <path d="M0,0 L4,2 L0,4 Z" fill="#a8a8a0" />
        </marker>
      </defs>
      <text x="10" y="9" fontFamily="'JetBrains Mono',monospace" fontSize="6.2" fill="#666">
        CUDA GEMM optimization
      </text>
      <rect x={ax} y={ay} width={aw} height={ah} fill="#c4d0c0" stroke="#aaa" strokeWidth="0.6" rx="1" />
      <rect x={ax + 4} y={ay + 4} width={20} height={10} fill="#8aaa88" opacity="0.6" rx="1" />
      <text x={ax + aw / 2} y={ay - 3} textAnchor="middle" fontFamily="'JetBrains Mono',monospace" fontSize="4.8" fill="#777">global</text>
      <rect x={sx} y={sy} width={sw} height={sh} fill="#cfd5df" stroke="#aaa" strokeWidth="0.6" rx="1" />
      <rect x={sx + 4} y={sy + 4} width={24} height={10} fill="#8c96bb" opacity="0.62" rx="1" />
      <text x={sx + sw / 2} y={sy - 3} textAnchor="middle" fontFamily="'JetBrains Mono',monospace" fontSize="4.8" fill="#777">shared</text>
      <rect x={tx} y={ty} width={tw} height={th} fill="#f1efe7" stroke="#aaa" strokeWidth="0.6" rx="1" />
      {Array.from({ length: 4 }, (_, i) => (
        <rect key={i} x={tx + 4 + i * 9} y={ty + 4 + (i % 2) * 12} width="7" height="7" fill="#b8a880" opacity="0.55" rx="0.8" />
      ))}
      <text x={tx + tw / 2} y={ty - 3} textAnchor="middle" fontFamily="'JetBrains Mono',monospace" fontSize="4.8" fill="#777">compute</text>
      <rect x={ox} y={oy} width={ow} height={oh} fill="#d4d0c4" stroke="#aaa" strokeWidth="0.6" rx="1" />
      <rect x={ox + 4} y={oy + 4} width="8" height={oh - 8} fill="#b8a880" opacity="0.45" rx="1" />
      <text x={ox + ow / 2} y={oy - 3} textAnchor="middle" fontFamily="'JetBrains Mono',monospace" fontSize="6.2" fill="#777">output C</text>
      <line x1={ax + aw} y1="37" x2={sx} y2="37" stroke="#a8a8a0" strokeWidth="0.8" markerEnd="url(#arrGemm)" />
      <line x1={sx + sw} y1="37" x2={tx} y2="37" stroke="#a8a8a0" strokeWidth="0.8" markerEnd="url(#arrGemm)" />
      <line x1={tx + tw} y1="37" x2={ox} y2="37" stroke="#a8a8a0" strokeWidth="0.8" markerEnd="url(#arrGemm)" />
      <text x="48" y="33" fontFamily="'JetBrains Mono',monospace" fontSize="4.2" fill="#888">load</text>
      <text x="112" y="33" fontFamily="'JetBrains Mono',monospace" fontSize="4.2" fill="#888">reuse</text>
      <text x="180" y="33" fontFamily="'JetBrains Mono',monospace" fontSize="4.2" fill="#888">accum</text>
      <text x="18" y="69" fontFamily="'JetBrains Mono',monospace" fontSize="4.6" fill="#888">64x64x8 tile</text>
      {strategies.map((s, i) => {
        const x = 248 + i * 18;
        const h = s.v * 20;
        return (
          <g key={s.label}>
            <rect x={x} y={60 - h} width="10" height={h} rx="1" fill="#333" opacity={0.45 + i * 0.12} />
            <text x={x + 5} y="66" textAnchor="middle" fontFamily="'JetBrains Mono',monospace" fontSize="3.8" fill="#888">
              {s.label === 'coal' ? 'coal' : s.label === 'smem' ? 'smem' : s.label === 'blk' ? 'block' : s.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/* ── OpenGL ECS Engine: entity-component diagram ─ */
const CodeGameEngineSVG: React.FC = () => {
  const W = 340, H = 68;
  const ph = 13; // pill height
  const pr = 2;  // pill radius

  type Comp = { label: string; fill: string; w: number };
  const rows: { id: string; comps: Comp[] }[] = [
    {
      id: 'e0',
      comps: [
        { label: 'Transform', fill: '#d0d0c8', w: 52 },
        { label: 'Mesh',      fill: '#c4ccd8', w: 28 },
        { label: 'Material',  fill: '#d4c8c4', w: 42 },
        { label: 'Physics',   fill: '#c8d4c4', w: 38 },
      ],
    },
    {
      id: 'e1',
      comps: [
        { label: 'Transform', fill: '#d0d0c8', w: 52 },
        { label: 'Light',     fill: '#dcd8b4', w: 30 },
        { label: 'Camera',    fill: '#c4d0d0', w: 36 },
      ],
    },
    {
      id: 'e2',
      comps: [
        { label: 'Transform', fill: '#d0d0c8', w: 52 },
        { label: 'Mesh',      fill: '#c4ccd8', w: 28 },
        { label: 'Material',  fill: '#d4c8c4', w: 42 },
      ],
    },
  ];

  const rowY = [11, 27, 43];
  const pillStart = 36;
  const pillGap = 4;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <rect width={W} height={H} fill="#eeeee9" />
      <Grid w={W} h={H} />

      {rows.map((row, ri) => {
        const y = rowY[ri];
        let px = pillStart;
        return (
          <g key={row.id}>
            {/* Entity ID */}
            <text x={8} y={y + ph / 2 + 2.5} fontFamily="'JetBrains Mono',monospace"
              fontSize="6.5" fill="#aaa">{row.id}</text>
            {/* Component pills */}
            {row.comps.map((c, ci) => {
              const x = px;
              px += c.w + pillGap;
              return (
                <g key={ci}>
                  <rect x={x} y={y} width={c.w} height={ph} fill={c.fill}
                    stroke="#c8c8c0" strokeWidth="0.5" rx={pr} />
                  <text x={x + c.w / 2} y={y + ph / 2 + 2.5} textAnchor="middle"
                    fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#555">
                    {c.label}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}

      {/* Separator lines between rows */}
      <line x1={6} y1={rowY[1] - 2} x2={220} y2={rowY[1] - 2} stroke="#d8d8d0" strokeWidth="0.4" />
      <line x1={6} y1={rowY[2] - 2} x2={220} y2={rowY[2] - 2} stroke="#d8d8d0" strokeWidth="0.4" />

      {/* Right-side annotations */}
      <text x={230} y={17} fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#999">OpenGL PBR</text>
      <text x={230} y={27} fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#aaa">ECS architecture</text>
      <text x={230} y={37} fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#bbb">async FBX loading</text>

      <text x={8} y={H - 4} fontFamily="'JetBrains Mono',monospace" fontSize="6" fill="#bbb">
        OpenGL engine · ECS · PBR materials · ImGui content browser
      </text>
    </svg>
  );
};

/* ── Preview Panel ─────────────────────────────── */

/* Shows screenshots on hover/pin; SVG diagram is always visible separately */
/* ── Data ─────────────────────────────────────── */

interface Project {
  id: string;
  name: string;
  badge: string;
  year: string;
  status: string;
  topic: 'rendering' | 'gpu' | 'ml';
  stack: string;
  description: string;
  problem: string;
  scopeSignals: string[];
  technicalApproach: string[];
  result: string;
  proofNow: string[];
  githubUrl: string;
  screenshots: string[];
  Diagram: React.FC;
  featured?: boolean;
}

const featuredProjects: Project[] = [
  {
    id: 'project-codecudaengine',
    name: 'CodeCudaEngine',
    badge: 'CU',
    year: '2025',
    status: 'Stable',
    topic: 'gpu',
    stack: 'CUDA · C++ · cuBLAS',
    description: 'CUDA GEMM kernel optimization study — four tiling strategies implemented from scratch and benchmarked against cuBLAS, with automated correctness verification on every run.',
    problem: 'The goal was to understand GEMM performance from first principles instead of treating cuBLAS as a black box.',
    scopeSignals: ['4 kernel variants', 'BM=64 BN=64 BK=8 tiling study', 'cuBLAS + CPU correctness checks'],
    technicalApproach: [
      'Kernel progression from naive coalesced loads to shared-memory tiled and block-tiled row/col strategies',
      'Shared memory tiling with BM=64 BN=64 BK=8 to study memory movement and data reuse',
      'Correctness validation against both cuBLAS and CPU reference using an element-wise error kernel',
      'CUDA Events timing with warmup runs and per-strategy GFLOPS reporting for disciplined measurement',
    ],
    result: 'This is the clearest compute-depth project in the portfolio: kernel evolution, memory hierarchy decisions, correctness validation, and benchmarking discipline all live in one place.',
    proofNow: [
      'Inline GEMM tiling diagram',
      'Repository-backed implementation and benchmarking setup',
    ],
    githubUrl: 'https://github.com/Carcodee/CodeCudaEngine',
    screenshots: [],
    Diagram: CodeCudaEngineSVG,
    featured: true,
  },
  {
    id: 'project-codevkengine',
    name: 'CodeVkEngine',
    badge: 'VK',
    year: '2025',
    status: 'Active',
    topic: 'rendering',
    stack: 'C++ · Vulkan · GLSL',
    description: 'High-performance Vulkan rendering engine built around a RenderGraph architecture for flexible, data-driven pass management.',
    problem: 'Needed a flexible rendering sandbox for graphics experimentation without hard-wiring passes and resources into a brittle frame pipeline.',
    scopeSignals: ['RenderGraph architecture', '1000+ dynamic lights', 'Hot reload + Radiance Cascades GI'],
    technicalApproach: [
      'RenderGraph architecture with pass and resource dependency tracking rather than manual frame wiring',
      'GPU-driven cluster lighting for 1000+ dynamic lights',
      'Frame architecture designed around resource flow, pass scheduling, and shader iteration speed',
      'Hot reload and Radiance Cascades GI used as rendering-systems validation, not just feature count',
    ],
    result: 'The project demonstrates rendering architecture depth: dependency management, frame structure, GPU-driven work submission, and tooling that supports iteration instead of one-off demos.',
    proofNow: [
      'Screenshot gallery of the renderer in use',
      'Inline RenderGraph architecture diagram',
      'Public repository with implementation details',
    ],
    githubUrl: 'https://github.com/Carcodee/CodeVkEngine',
    screenshots: ['/images/vk1.jpg', '/images/vk2.jpg', '/images/vk3.jpg'],
    Diagram: RenderGraphSVG,
    featured: true,
  },
  {
    id: 'project-vulkan-path-tracer',
    name: 'Vulkan Path Tracer',
    badge: 'RT',
    year: '2024',
    status: 'Stable',
    topic: 'rendering',
    stack: 'C++ · Vulkan RT · PBR',
    description: 'Monte Carlo path tracer using Vulkan RT extensions with global illumination and physically-based materials.',
    problem: 'Built to explore physically based ray tracing in Vulkan RT while keeping memory usage and scene complexity practical.',
    scopeSignals: ['Vulkan RT pipeline', '~15% memory reduction', '1000+ point lights'],
    technicalApproach: [
      'Vulkan RT pipeline using hardware BVH traversal and RT cores',
      'Disney principled BSDF implementation for physically based shading behavior',
      '~15% memory reduction through renderer resource optimization',
      'GPU-driven occlusion culling while scaling to 1000+ point lights',
    ],
    result: 'Shows low-level rendering sophistication across RT pipeline setup, traversal cost, shading implementation, visibility work, and memory tradeoffs.',
    proofNow: [
      'Seven-image gallery of the renderer output',
      'Inline path tracing and BSDF diagram',
      'Public repository for code inspection',
    ],
    githubUrl: 'https://github.com/Carcodee/CodeRaytracer',
    featured: true,
    screenshots: ['/images/rt1.jpg','/images/rt2.jpg','/images/rt3.jpg','/images/rt4.jpg','/images/rt5.jpg','/images/rt6.jpg','/images/rt7.jpg'],
    Diagram: PathTracerSVG,
  },
  {
    id: 'project-gpu-benchmark-suite',
    name: 'GPU Benchmark Suite',
    badge: 'GPU',
    year: '2024',
    status: 'Stable',
    topic: 'gpu',
    stack: 'Python · OpenCL · Compute',
    description: 'Toolkit for measuring GPU memory bandwidth, occupancy, and kernel throughput across workgroup configurations.',
    problem: 'Needed a repeatable way to inspect bandwidth, occupancy, and workgroup behavior instead of tuning compute code blindly.',
    scopeSignals: ['Workgroup sweep 1-64', 'Occupancy comparisons', 'Bandwidth + bank-conflict analysis'],
    technicalApproach: [
      'Bandwidth and latency measurement across different compute patterns',
      'Workgroup size sweeps from 1 to 64 across kernel configurations',
      'Occupancy comparison between high shared-memory and zero shared-memory kernels',
      'Bank conflict inspection in shared memory to reason about kernel behavior under constraints',
    ],
    result: 'This project is performance methodology: measure bandwidth, occupancy, and shared-memory effects first, then tune from evidence rather than intuition.',
    proofNow: [
      'Inline benchmark diagram showing the measured dimensions',
      'Repository for the OpenCL and Python tooling',
    ],
    githubUrl: 'https://github.com/Carcodee/GPU-programming-benchmark',
    screenshots: [],
    Diagram: BenchmarkSVG,
    featured: true,
  },
];

const supportingProjects: Project[] = [
  {
    id: 'project-code-game-engine',
    name: 'Code-Game-Engine',
    badge: 'GL',
    year: '2024',
    status: 'Stable',
    topic: 'rendering',
    stack: 'C++ · OpenGL · ECS',
    description: 'OpenGL 3D game engine with an Entity-Component System, PBR material pipeline, and ImGui-based content browser.',
    problem: 'Built to understand core engine systems directly: scene composition, editor tooling, asset flow, and real-time shading.',
    scopeSignals: ['ECS scene architecture', 'PBR + shadow pass', 'Async FBX import'],
    technicalApproach: [
      'Entity-Component System architecture for flexible scene composition',
      'PBR material pipeline with physically-based shading and bloom',
      'Drag-and-drop FBX loading with async model import',
      'ImGui content browser, transform hierarchy, and shadow pass',
    ],
    result: 'Useful supporting proof that your graphics work is not limited to isolated shaders or samples; you have engine-system depth as well.',
    proofNow: [
      'Inline engine architecture diagram',
      'Repository for the engine and editor code',
    ],
    githubUrl: 'https://github.com/Carcodee/Code-Game-Engine',
    screenshots: ['/images/gl1.jpg', '/images/gl2.jpg', '/images/gl3.jpg'],
    Diagram: CodeGameEngineSVG,
  },
];

const researchProjects: Project[] = [
  {
    id: 'project-transformer-llm',
    name: 'Transformer LLM',
    badge: 'AI',
    year: '2024',
    status: 'Research',
    topic: 'ml',
    stack: 'Python · PyTorch · AI',
    description: 'Language model implemented from scratch with a byte-level tokenizer, multi-head self-attention, and a two-stage training pipeline.',
    problem: 'Built to understand transformer internals by implementing tokenizer, attention blocks, and training flow directly instead of treating the stack as a black box.',
    scopeSignals: ['Byte-level BPE tokenizer', '8 heads · 6 blocks', '256-token context · 512-dim embeddings'],
    technicalApproach: [
      'Byte-level BPE tokenizer trained from scratch',
      'Multi-head causal self-attention stack with 8 heads and 6 blocks',
      '256-token context and 512-dim embeddings for a compact research-scale model',
      'Instruction-tuning stage using a structured <user>/<bot> conversation format',
    ],
    result: 'Useful as research proof of systems curiosity and model internals knowledge, while remaining secondary to the core GPU and rendering portfolio identity.',
    proofNow: [
      'Inline transformer architecture diagram',
      'Repository for the model and tokenizer implementation',
    ],
    githubUrl: 'https://github.com/Carcodee/LLMFromScratch',
    screenshots: [],
    Diagram: TransformerSVG,
  },
];

/* ── Component ─────────────────────────────────── */

const Projects: React.FC = () => {
  const groups = useMemo(() => ([
    { label: 'Core GPU & Rendering Work', projects: featuredProjects },
    { label: 'Supporting Systems Work', projects: supportingProjects },
    { label: 'ML Research', projects: researchProjects },
  ]), []);
  const allProjects = useMemo(() => groups.flatMap((group) => group.projects), [groups]);
  const [selectedId, setSelectedId] = useState<string>(featuredProjects[1]?.id ?? featuredProjects[0].id);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const selectedProject = allProjects.find((project) => project.id === selectedId) ?? allProjects[0];

  useEffect(() => {
    const detail = detailRef.current;
    if (!detail || window.innerWidth > 768) {
      return;
    }

    const rect = detail.getBoundingClientRect();
    const topOffset = 72;
    const targetTop = Math.max(window.scrollY + rect.top - topOffset, 0);
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  }, [selectedId]);

  const renderProjectRow = (project: Project) => {
    const isSelected = project.id === selectedId;
    const stackTags = project.stack.split(' · ');
    const PreviewDiagram = project.Diagram;

    return (
      <button
        key={project.id}
        type="button"
        className={`project-list-item${isSelected ? ' selected' : ''}${project.featured ? ' featured' : ''}`}
        data-topic={project.topic}
        onClick={() => setSelectedId(project.id)}
        aria-pressed={isSelected}
      >
        <div className="project-list-body">
          <div className="project-list-copy">
            <div className="project-list-head">
              <span className="entry-title project-title-row">
                <span className="project-topic-badge" data-topic={project.topic}>{project.badge}</span>
                {project.name}
              </span>
              <span className="entry-meta">{project.year}</span>
            </div>
            <div className="entry-sub project-summary">{project.description}</div>
            <div className="project-stack-row">
              {stackTags.map((tag) => (
                <span key={tag} className="project-stack-tag">{tag}</span>
              ))}
            </div>
          </div>
          <div className={`project-list-preview${project.screenshots.length > 0 ? ' has-image' : ' has-diagram'}`}>
            {project.screenshots.length > 0 ? (
              <img
                src={project.screenshots[0]}
                alt={`${project.name} preview`}
                loading="lazy"
                className="project-list-preview-image"
              />
            ) : (
              <PreviewDiagram />
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <section id="projects" data-topic="rendering">
      <div className="content projects-content">
        <h2 className="section-heading">Projects</h2>
        <div className="projects-master-detail">
          <div className="projects-list-column">
            {groups.map((group) => (
              <div key={group.label} className="projects-group">
                <div className="projects-group-label">{group.label}</div>
                <div className="projects-group-list">
                  {group.projects.map(renderProjectRow)}
                </div>
              </div>
            ))}
          </div>

          <div ref={detailRef} className="projects-detail-column">
            <div className="projects-detail-panel" data-topic={selectedProject.topic}>
              <div className="projects-detail-header">
                <div className="projects-detail-title-row">
                  <span className="project-topic-badge" data-topic={selectedProject.topic}>
                    {selectedProject.badge}
                  </span>
                  <h3 className="projects-detail-title">{selectedProject.name}</h3>
                </div>
                <div className="projects-detail-meta-row">
                  <span className="entry-meta">{selectedProject.year}</span>
                  <span className="badge">{selectedProject.status}</span>
                </div>
              </div>

              <div className="entry-sub project-summary">{selectedProject.description}</div>
              <div className="project-stack-row">
                {selectedProject.stack.split(' · ').map((tag) => (
                  <span key={tag} className="project-stack-tag">{tag}</span>
                ))}
              </div>
              <div className="projects-detail-signals">
                {selectedProject.scopeSignals.map((signal) => (
                  <span key={signal} className="project-signal">{signal}</span>
                ))}
              </div>

              {selectedProject.screenshots.length > 0 && (
                <Gallery images={selectedProject.screenshots} />
              )}

              <div className="project-case-grid">
                <div className="project-case-block">
                  <div className="project-case-label">Challenge</div>
                  <p className="project-case-text">{selectedProject.problem}</p>
                </div>

                <div className="project-case-block">
                  <div className="project-case-label">Approach</div>
                  <div className="entry-points" style={{ marginTop: '0.45rem' }}>
                    {selectedProject.technicalApproach.map((point, index) => (
                      <div key={index} className="entry-point">{point}</div>
                    ))}
                  </div>
                </div>

                <div className="project-case-block">
                  <div className="project-case-label">Result</div>
                  <p className="project-case-text">{selectedProject.result}</p>
                </div>

                <div className="project-case-block">
                  <div className="project-case-label">Links</div>
                  <div className="entry-points" style={{ marginTop: '0.45rem' }}>
                    {selectedProject.proofNow.map((proof, index) => (
                      <div key={index} className="entry-point">{proof}</div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="project-diagram-block">
                <div className="project-diagram-label">Architecture Diagram</div>
                <div className="project-preview project-preview-expanded">
                  <selectedProject.Diagram />
                </div>
              </div>

              <div className="project-links-row">
                <a
                  href={selectedProject.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-btn"
                >
                  GitHub ↗
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
